
import { useState } from "react";
import { Mail, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { z } from "zod";

const createBasicContactSchema = (language: string) => z.object({
  name: z.string()
    .trim()
    .min(1, { message: language === 'en' ? "Name is required" : "Név megadása kötelező" })
    .max(100, { message: language === 'en' ? "Name must be less than 100 characters" : "A név maximum 100 karakter lehet" }),
  email: z.string()
    .trim()
    .email({ message: language === 'en' ? "Invalid email address" : "Érvénytelen email cím" })
    .max(255, { message: language === 'en' ? "Email must be less than 255 characters" : "Az email maximum 255 karakter lehet" }),
  message: z.string()
    .trim()
    .min(1, { message: language === 'en' ? "Message is required" : "Üzenet megadása kötelező" })
    .max(2000, { message: language === 'en' ? "Message must be less than 2000 characters" : "Az üzenet maximum 2000 karakter lehet" })
});

interface BasicFormData {
  name: string;
  email: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

export const BasicContactForm = () => {
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [formData, setFormData] = useState<BasicFormData>({
    name: "",
    email: "",
    message: ""
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    // Validate form data
    const schema = createBasicContactSchema(language);
    const result = schema.safeParse(formData);
    
    if (!result.success) {
      const fieldErrors: FormErrors = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as keyof FormErrors;
        fieldErrors[field] = err.message;
      });
      setErrors(fieldErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const validatedData = result.data;
      
      // Use edge function with rate limiting
      const { data, error } = await supabase.functions.invoke('submit-contact', {
        body: {
          name: validatedData.name,
          email: validatedData.email,
          message: validatedData.message,
        }
      });
      
      if (error) {
        console.error('Error submitting form:', error);
        toast({
          title: language === 'en' ? "An error occurred!" : "Hiba történt!",
          description: language === 'en' 
            ? "We couldn't process your message. Please try again later." 
            : "Nem sikerült feldolgozni az üzenetet. Kérjük, próbálja újra később.",
          variant: "destructive",
        });
        return;
      }

      // Check for rate limiting response
      if (data?.error) {
        if (data.retryAfter) {
          toast({
            title: language === 'en' ? "Too many requests" : "Túl sok kérés",
            description: language === 'en'
              ? `Please wait ${data.retryAfter} seconds before trying again.`
              : `Kérjük, várjon ${data.retryAfter} másodpercet, mielőtt újra próbálkozna.`,
            variant: "destructive",
          });
        } else {
          toast({
            title: language === 'en' ? "An error occurred!" : "Hiba történt!",
            description: data.error,
            variant: "destructive",
          });
        }
        return;
      }

      toast({
        title: language === 'en' ? "Message sent!" : "Üzenet elküldve!",
        description: language === 'en'
          ? "Thank you for your message! We'll get back to you shortly."
          : "Köszönjük üzenetét! Hamarosan felvesszük Önnel a kapcsolatot."
      });

      setFormData({
        name: "",
        email: "",
        message: ""
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: language === 'en' ? "An error occurred!" : "Hiba történt!",
        description: language === 'en'
          ? "We couldn't process your message. Please try again later."
          : "Nem sikerült feldolgozni az üzenetet. Kérjük, próbálja újra később.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('contact_basic_name')}
          </label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <User className="w-4 h-4" />
            </div>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              className={`w-full pl-10 px-4 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50`}
            />
          </div>
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
        </div>
        
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('contact_basic_email')}
          </label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <Mail className="w-4 h-4" />
            </div>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              className={`w-full pl-10 px-4 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50`}
            />
          </div>
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>
        
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('contact_basic_message')}
          </label>
          <Textarea
            id="message"
            name="message"
            required
            value={formData.message}
            onChange={handleChange}
            className={`w-full px-4 py-2 min-h-[120px] border ${errors.message ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50`}
          />
          {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
        </div>
        
        <div>
          <Button 
            type="submit" 
            className="w-full bg-primary hover:bg-primary/90 text-white"
            disabled={isSubmitting}
          >
            {isSubmitting 
              ? (language === 'en' ? "Sending..." : "Küldés...") 
              : (language === 'en' ? "Send Message" : "Üzenet küldése")
            }
          </Button>
        </div>
        
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 text-center">
          {t('contact_basic_privacy')} <Link to="/adatkezeles" className="text-primary hover:underline">{t('privacy_policy_link')}</Link>.
        </p>
      </div>
    </form>
  );
};
